import { MODE } from './types';

/** 유효한 모드 타입 (빈 문자열 제외) */
export type ValidMode = (typeof MODE)[keyof typeof MODE];

/**
 * 모드별 UI 구성 설정
 *
 * 각 모드에서 어떤 UI 요소가 활성화되는지 한눈에 파악 가능
 */
export interface ModeConfig {
  /** LiveUpdater 활성화 여부 (실시간 시간 갱신) */
  useLiveUpdater: boolean;
  /** TimePeriodDisplay 사용 여부 (CURRENT 모드 전용 심플 표시) */
  useTimePeriodDisplay: boolean;
  /** Selector 타입: 'period' | 'calendar' | null */
  selectorType: 'period' | 'calendar' | null;
  /** LiveTag 표시 여부 */
  showLiveTag: boolean;
  /** CalendarSelector에서 입력 필드 표시 여부 */
  showCalendarInput: boolean;
  /** TimeControls 설정 */
  controls: {
    showNavigator: boolean;
    showRefresh: boolean;
    alwaysShowPlay: boolean;
    showPause?: boolean;
  };
}

/**
 * 모드별 UI 구성 매핑
 *
 * @example
 * ```
 * // 특정 모드의 설정 조회
 * const config = MODE_CONFIG[MODE.CURRENT];
 * console.log(config.useLiveUpdater); // true
 * ```
 */
export const MODE_CONFIG: Record<ValidMode, ModeConfig> = {
  /**
   * CURRENT: 현재 시간만 표시
   * - Live 갱신 O, 기간 선택 X, 네비게이션 X
   */
  [MODE.CURRENT]: {
    useLiveUpdater: true,
    useTimePeriodDisplay: true,
    selectorType: null,
    showLiveTag: false,
    showCalendarInput: false,
    controls: {
      showNavigator: false,
      showRefresh: false,
      alwaysShowPlay: false,
    },
  },

  /**
   * PERIOD: 기간만 선택 (Live 없음)
   * - Live 갱신 X, Period 드롭다운 O, 새로고침 O, 재생 버튼 항상 표시
   */
  [MODE.PERIOD]: {
    useLiveUpdater: false,
    useTimePeriodDisplay: false,
    selectorType: 'period',
    showLiveTag: true,
    showCalendarInput: false,
    controls: {
      showNavigator: true,
      showRefresh: true,
      alwaysShowPlay: true,
    },
  },

  /**
   * CURRENT_AND_PERIOD: Live + Period 선택
   * - Live 갱신 O, Period 드롭다운 O, 새로고침 X
   */
  [MODE.CURRENT_AND_PERIOD]: {
    useLiveUpdater: true,
    useTimePeriodDisplay: false,
    selectorType: 'period',
    showLiveTag: true,
    showCalendarInput: false,
    controls: {
      showNavigator: true,
      showRefresh: false,
      alwaysShowPlay: false,
    },
  },

  /**
   * CURRENT_AND_PERIOD_QUICK: Live + Period 선택 + 빠른 시간 범위
   * - Live 갱신 O, Period 드롭다운 O (Quick Ranges 포함), 새로고침 X
   */
  [MODE.CURRENT_AND_PERIOD_QUICK]: {
    useLiveUpdater: true,
    useTimePeriodDisplay: false,
    selectorType: 'period',
    showLiveTag: true,
    showCalendarInput: false,
    controls: {
      showNavigator: false,
      showRefresh: false,
      alwaysShowPlay: false,
    },
  },

  /**
   * PERIOD_CALENDAR: 캘린더로 기간 선택 (Live 없음)
   * - Live 갱신 X, Calendar O, 새로고침 O, 재생 버튼 항상 표시
   */
  [MODE.PERIOD_CALENDAR]: {
    useLiveUpdater: false,
    useTimePeriodDisplay: false,
    selectorType: 'calendar',
    showLiveTag: true,
    showCalendarInput: false,
    controls: {
      showNavigator: true,
      showRefresh: true,
      alwaysShowPlay: true,
    },
  },

  /**
   * PERIOD_CALENDAR_INPUT: 캘린더 + 입력 필드 (Live 없음)
   * - Live 갱신 X, Calendar O (입력 필드 포함), 새로고침 O, 재생 버튼 항상 표시
   */
  [MODE.PERIOD_CALENDAR_INPUT]: {
    useLiveUpdater: false,
    useTimePeriodDisplay: false,
    selectorType: 'calendar',
    showLiveTag: true,
    showCalendarInput: true,
    controls: {
      showNavigator: true,
      showRefresh: true,
      alwaysShowPlay: true,
    },
  },

  /**
   * CURRENT_PERIOD_CALENDAR: Live + Calendar 선택
   * - Live 갱신 O, Calendar O, 새로고침 X
   */
  [MODE.CURRENT_PERIOD_CALENDAR]: {
    useLiveUpdater: true,
    useTimePeriodDisplay: false,
    selectorType: 'calendar',
    showLiveTag: true,
    showCalendarInput: false,
    controls: {
      showNavigator: true,
      showRefresh: false,
      alwaysShowPlay: false,
    },
  },

  /**
   * CURRENT_PERIOD_CALENDAR_INPUT: Live + Calendar + 입력 필드
   * - Live 갱신 O, Calendar O (입력 필드 포함), 새로고침 X
   */
  [MODE.CURRENT_PERIOD_CALENDAR_INPUT]: {
    useLiveUpdater: true,
    useTimePeriodDisplay: false,
    selectorType: 'calendar',
    showLiveTag: true,
    showCalendarInput: true,
    controls: {
      showNavigator: true,
      showRefresh: false,
      alwaysShowPlay: false,
    },
  },

  /**
   * PERIOD_CALENDAR_INPUT_WITHOUT_NAVIGATOR: 캘린더 + 구간 입력 필드 + 조작 버튼 없음
   * - Live 갱신 X, Calendar O (입력 필드 포함),
   */
  [MODE.PERIOD_CALENDAR_INPUT_WITHOUT_NAVIGATOR]: {
    useLiveUpdater: false,
    useTimePeriodDisplay: false,
    selectorType: 'calendar',
    showLiveTag: true,
    showCalendarInput: true,
    controls: {
      showNavigator: false,
      showRefresh: false,
      showPause: false,
      alwaysShowPlay: false,
    },
  },
};
